<template>
	<div class="company-profile-page">
		<DcHeader :title="columnTitleName" :isShare="true">
			<template #dropdown>
				<DropDown :show="true" />
			</template>
		</DcHeader>

		<div class="main">
			<div v-if="companyText" class="content-card">
				<div class="m-richtext" v-html="companyText"></div>
			</div>

			<div v-else-if="!pending" class="no-data">
				<span>抱歉，暂无相关数据</span>
			</div>
			<div v-else class="loading-hint">加载中…</div>
		</div>

		<SubFoot />
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useSeoMeta } from '#imports';
import DcHeader from '@/components/DcHeader.vue';
import DropDown from '@/components/DropDown.vue';
import SubFoot from '@/components/SubFoot.vue';
import { detailCompany } from '@/api/childSite.js';
import { queryWebsiteByCompanyId } from '@/api/index.js';

/** 与 goods/index.vue 一致：用 computed + watch，避免首屏 route 未就绪时写死空 id 导致永不请求 */
const route = useRoute();
const companyId = computed(() => String(route.params.companyId ?? route.query.companyId ?? ''));

function plainFromHtml(html) {
	if (!html) return '';
	return String(html)
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

const { data: pageData, pending } = await useAsyncData(
	() => `company-profile-${companyId.value}`,
	async () => {
		const cid = companyId.value;
		if (!cid) return { artRes: null, siteRes: null };

		const payload = { companyId: cid, wzType: '3', disableSign: 0 };
		const [artRes, siteRes] = await Promise.all([detailCompany(payload), queryWebsiteByCompanyId(cid).catch(() => null)]);
		return { artRes, siteRes };
	},
	{ watch: [companyId] }
);

const articleRow = computed(() => {
	const res = pageData.value?.artRes;
	const row = res?.data?.list?.[0];
	return res?.code == 200 && row ? row : null;
});

/** 导航标题：`artRes.data.list[0]` 的 companyName，否则 websiteName，无则「公司简介」 */
const columnTitleName = computed(() => {
	const row = articleRow.value;
	const s = String(row?.companyName || row?.websiteName || '').trim();
	return s || '公司简介';
});

/** 普通站点：正文 list[0].content（移动端不展示独立配图字段，正文内嵌图仍由 content 的 HTML 渲染） */
const companyText = computed(() => {
	const row = articleRow.value;
	if (!row) return '';
	const c = row.content;
	return c == null ? '' : String(c);
});

const websiteName = computed(() => pageData.value?.siteRes?.data?.websiteName || '');

const seoTitle = computed(() => {
	const name = websiteName.value;
	return name ? `公司介绍-${name}` : '公司介绍';
});

const seoDescription = computed(() => {
	const plain = plainFromHtml(companyText.value);
	if (plain) {
		return plain.length > 150 ? `${plain.slice(0, 150)}...` : plain;
	}
	const name = websiteName.value;
	return name ? `${name}的公司介绍信息，欢迎访问国械在线。` : '公司介绍信息，欢迎访问国械在线。';
});

useSeoMeta({
	title: () => seoTitle.value,
	description: () => seoDescription.value,
});
</script>

<style lang="scss" scoped>
.company-profile-page {
	min-height: 100vh;
	padding-bottom: calc(4.17rem + env(safe-area-inset-bottom));
}

.main {
	min-height: calc(100vh - 50px);
}

.content-card {
	margin-top: 0.5rem;
	padding: 1.25rem;
	background: #fff;
}

.no-data {
	padding: 3rem 1rem;
	text-align: center;
	color: #999;
	font-size: 1rem;
}
</style>
